/**
 * FoliageTranslucency — cheap two-sided translucent lighting for leaves,
 * paper, curtains: the classic back-transmission term
 *
 *   TT = pow(saturate(V · −L), backPower) · backIntensity
 *
 * added as emissive tinted by the translucency colour, plus a wrap factor
 * that lifts the terminator (half-Lambert family). The light direction is a
 * UNIFORM (the sun/key light) — foliage TT tracks the dominant light, not
 * the whole light list, which is the standard real-time approximation.
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, positionViewDirection, normalView, positionGeometry, diffuseColor,
  pow, saturate, normalize, mix, max, smoothstep, varying,
} from 'three/tsl';

export interface FoliageTranslucencyConfig {
  /** Transmission tint (default leafy 0.35, 0.55, 0.12). */
  color?: [number, number, number];
  /** View-alignment exponent — higher = tighter sun-behind-leaf glow (default 4). */
  backPower?: number;
  /** Transmission strength (default 0.6). */
  backIntensity?: number;
  /**
   * Incident light intensity (default 1) and colour (default white). The
   * transmission is LIGHT passing through the leaf: without these the term was
   * a constant glow that ignored the sun going down (audit 16.2, 2026-09-15).
   */
  lightIntensity?: number;
  lightColor?: [number, number, number];
  /** Light direction in VIEW space (default sun-ish 0.3,0.5,0.8). */
  lightDirView?: [number, number, number];
  /** Terminator wrap 0..1 added to N·L shaping (default 0.4). */
  wrap?: number;
  /**
   * How far the transmitted colour follows the surface's own ALBEDO (0 = the flat `color` tint — the old look;
   * 1 = albedo × `albedoGain` × a slight yellowing). A leaf transmits roughly what it reflects (Habel 2007: T ≈ R across
   * the visible band, T a little yellower), so a textured crown glows with its texture's structure instead of as one
   * flat colour — the back-lit canopy read as a uniform olive silhouette (surfaces, 2026-09-27). Default 0.
   */
  albedoTint?: number;
  /** Gain on the albedo in albedo mode (default 2.2 — keeps a typical leaf albedo near the calibrated tint's level). */
  albedoGain?: number;
  /**
   * A CROWN MASK in model space (positionGeometry — the raw attribute; `positionLocal` is already instance-transformed
   * inside a material, THE positionGeometry LAW). One material draws trunk and leaves in the cooked trees, and bark does
   * not transmit: the glow fades out near the trunk axis below the crown. `CrownParams` from the geometry
   * (`crownParamsFromPositions`). Omitted = no mask.
   */
  crown?: CrownParams;
}

/** The crown mask's geometry, all in the mesh's MODEL space. */
export interface CrownParams {
  /** The mesh's lowest y and its height (m). */
  baseY: number;
  height: number;
  /** The trunk axis (x, z) and the crown's horizontal radius (m). */
  centerX: number;
  centerZ: number;
  radius: number;
  /** Height fraction (0..1) where the crown begins — below it only what stands off the axis glows. */
  start: number;
  /** The trunk's radius as a fraction of `radius`. */
  trunkR: number;
}

export interface FoliageTranslucencyHandle {
  color: { value: THREE.Vector3 };
  backPower: { value: number };
  backIntensity: { value: number };
  lightIntensity: { value: number };
  lightColor: { value: THREE.Vector3 };
  lightDirView: { value: THREE.Vector3 };
  wrap: { value: number };
  /** 0..1 — the albedo-tinted transmission's share (a live A/B lever). */
  albedoTint: { value: number };
  /** 0..1 — the crown mask's strength (0 = every texel transmits; only meaningful with `crown`). */
  crownMix: { value: number };
}

/**
 * CPU mirror of the crown mask: `h` = height fraction over the mesh, `r` = distance from the trunk axis ÷ the crown
 * radius. 1 inside the crown or off the axis; 0 on the trunk below the crown.
 */
export function crownMaskCpu(h: number, r: number, start: number, trunkR: number): number {
  const ss = (e0: number, e1: number, x: number): number => { const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };
  return Math.max(ss(start - 0.08, start, h), ss(trunkR * 1.2, trunkR * 2.2, r));
}

/**
 * Derive the crown mask's parameters from a mesh's POSITION array (x, y, z triples, model space). The trunk axis is the
 * centroid of the lowest 15 % of vertices; the radius is the widest height band's 90th-percentile distance from it;
 * the crown starts at the lowest band whose 90th percentile passes 45 % of that; the trunk's radius is the lowest
 * band's 75th percentile. Null for an empty or flat mesh.
 */
export function crownParamsFromPositions(pos: ArrayLike<number>, stride = 3): CrownParams | null {
  const n = Math.floor(pos.length / stride);
  if (n < 8) return null;
  let minY = Infinity, maxY = -Infinity;
  for (let i = 0; i < n; i++) { const y = pos[i * stride + 1]; if (y < minY) minY = y; if (y > maxY) maxY = y; }
  const height = maxY - minY;
  if (!(height > 1e-3)) return null;
  const lowCut = minY + height * 0.15;
  let cx = 0, cz = 0, cn = 0;
  for (let i = 0; i < n; i++) { if (pos[i * stride + 1] <= lowCut) { cx += pos[i * stride]; cz += pos[i * stride + 2]; cn++; } }
  if (cn === 0) return null;
  cx /= cn; cz /= cn;
  const BANDS = 12;
  const bands: number[][] = Array.from({ length: BANDS }, () => []);
  for (let i = 0; i < n; i++) {
    const y = pos[i * stride + 1];
    const b = Math.min(BANDS - 1, Math.floor(((y - minY) / height) * BANDS));
    bands[b].push(Math.hypot(pos[i * stride] - cx, pos[i * stride + 2] - cz));
  }
  const pct = (a: number[], q: number): number => { if (!a.length) return 0; const s = a.slice().sort((p, q2) => p - q2); return s[Math.min(s.length - 1, Math.floor(q * (s.length - 1)))]; };
  const p90 = bands.map((a) => pct(a, 0.9));
  const radius = Math.max(...p90);
  if (!(radius > 1e-3)) return null;
  let start = 1;
  for (let b = 0; b < BANDS; b++) if (p90[b] > 0.45 * radius) { start = b / BANDS; break; }
  const trunkR = Math.min(0.3, Math.max(0.03, pct(bands[0], 0.75) / radius));
  return { baseY: minY, height, centerX: cx, centerZ: cz, radius, start, trunkR };
}

/** CPU mirror of the back-transmission term (exported for tests). */
export function backTransmissionCpu(
  vDotNegL: number, backPower: number, backIntensity: number,
): number {
  return Math.pow(Math.max(vDotNegL, 0), backPower) * backIntensity;
}

/** CPU mirror of wrap-shaped N·L (exported for tests). */
export function wrapDiffuseCpu(nDotL: number, wrap: number): number {
  const w = Math.min(Math.max(wrap, 0), 1);
  return Math.max((nDotL + w) / (1 + w), 0);
}

export function applyFoliageTranslucency(
  material: THREE.Material & { emissiveNode?: unknown; side?: THREE.Side },
  config?: FoliageTranslucencyConfig,
): FoliageTranslucencyHandle {
  const c = config?.color ?? [0.35, 0.55, 0.12];
  const ld = config?.lightDirView ?? [0.3, 0.5, 0.8];
  const uColor = uniform(vec3(c[0], c[1], c[2]));
  const uBackPower = uniform(config?.backPower ?? 4);
  const uBackIntensity = uniform(config?.backIntensity ?? 0.6);
  const uLightDir = uniform(vec3(ld[0], ld[1], ld[2]));
  const uWrap = uniform(config?.wrap ?? 0.4);
  const lc = config?.lightColor ?? [1, 1, 1];
  const uLightIntensity = uniform(config?.lightIntensity ?? 1);
  const uLightColor = uniform(vec3(lc[0], lc[1], lc[2]));
  const uAlbedoTint = uniform(config?.albedoTint ?? 0);
  const uAlbedoGain = uniform(config?.albedoGain ?? 2.2);
  const cr = config?.crown ?? null;
  const uCrownMix = uniform(cr ? 1 : 0);
  // surfaces (2026-09-27): the crown mask is computed in the VERTEX stage (one float varying — the foliage batches sit at
  // WebGPU's limits, and a vec3 position varying would cost three components for the same answer).
  const crownV = cr
    ? varying(Fn(() => {
      const h = positionGeometry.y.sub(cr.baseY).div(Math.max(1e-3, cr.height));
      const r = vec2(positionGeometry.x.sub(cr.centerX), positionGeometry.z.sub(cr.centerZ)).length().div(Math.max(1e-3, cr.radius));
      return max(smoothstep(cr.start - 0.08, cr.start, h), smoothstep(cr.trunkR * 1.2, cr.trunkR * 2.2, r));
    })(), 'vFoliageCrown')
    : null;

  const prevEmissive = material.emissiveNode;
  material.emissiveNode = Fn(() => {
    const l = normalize(uLightDir);
    // Sun shining THROUGH the leaf toward the eye: V aligned with −L.
    const vDotNegL = saturate(positionViewDirection.dot(l.negate()));
    const tt = pow(vDotNegL, uBackPower).mul(uBackIntensity);
    // Wrap lift: brightens the terminator-side transmission so backlit
    // edges glow before the exact anti-light alignment.
    const nDotL = normalView.dot(l);
    const wrapped = saturate(nDotL.add(uWrap).div(float(1).add(uWrap)));
    const base = (prevEmissive ?? vec3(0, 0, 0)) as never;
    // surfaces (2026-09-27): the transmitted colour follows the albedo (T ≈ R, a little yellower) by `albedoTint`, and
    // the crown mask keeps the bark dark — a flat constant tint over trunk and leaves was the back-lit silhouette.
    const albedoT = diffuseColor.rgb.mul(uAlbedoGain).mul(vec3(1.05, 1.0, 0.8));
    const tcol = mix(uColor, albedoT, saturate(uAlbedoTint));
    const mask = crownV ? mix(float(1), crownV, saturate(uCrownMix)) : float(1);
    // Scaled by the INCIDENT light: no sun, no transmission.
    return vec3(base).add(tcol.mul(uLightColor).mul(uLightIntensity).mul(tt.add(wrapped.mul(uBackIntensity).mul(0.15))).mul(mask));
  })();
  material.needsUpdate = true;

  return {
    color: uColor as unknown as { value: THREE.Vector3 },
    backPower: uBackPower as unknown as { value: number },
    backIntensity: uBackIntensity as unknown as { value: number },
    lightDirView: uLightDir as unknown as { value: THREE.Vector3 },
    wrap: uWrap as unknown as { value: number },
    lightIntensity: uLightIntensity as unknown as { value: number },
    lightColor: uLightColor as unknown as { value: THREE.Vector3 },
    albedoTint: uAlbedoTint as unknown as { value: number },
    crownMix: uCrownMix as unknown as { value: number },
  };
}
